import { Stack, Tooltip, Typography } from '@mui/material'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { EmptyList } from 'components/core/empty-list'
import { C1Icon } from 'components/core/icons/C1Icon'
import List from 'components/core/list'
import { C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { C1TableReduxHook } from 'components/core/list/C1Table/C1TableReduxHook'
import { RelativeTimestamp } from 'components/core/time/display'
import { isWithinXDays } from 'components/no-ui/helpers/timestampHelper'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { AppUserDisplayNameWithFetch } from 'components/pages/app/users/app-user-display-name'
import { AppSecretsFilter } from 'components/pages/app-resources/search/AppSecretFilters'
import { ResourceTypeDisplayName } from 'components/pages/entitlement/resource-type'
import { InventoryAlertNotification } from 'components/pages/explore/inventory/inventory_tabs/InventoryAlertNotification'
import { TriangleAlert } from 'lucide-react'
import { AppResourceType } from 'pbts/c1/api/app/v1/app_resource'
import { SearchAppSecretsWithAlertCountRequest } from 'pbts/c1/api/app/v1/app_secret'
import { AppUserRef } from 'pbts/c1/api/app/v1/app_user'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { FC, ReactElement, useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { AppSecretResourceValue, searchAppSecretsApi } from 'redux_toolkit/app-secrets'

export type AppSecretResourcesCols = 'name' | 'app' | 'type' | 'identity' | 'lastUsedAt' | 'expiresAt'

export type AppResourcesPropTypes = {
    appId?: string,
    identityAppUserRefs?: Array<AppUserRef>,
    colsToHide?: Array<AppSecretResourcesCols>,
}

const cols: C1TableReduxColumn<AppSecretResourceValue>[] = [
    {
        field: 'name',
        label: <FormattedMessage id="secret_name" defaultMessage="Name" />,
        Component: ({ row }): ReactElement => <Typography>{row.displayName}</Typography>,
    },
    {
        field: 'app',
        label: <FormattedMessage id="secret_app" defaultMessage="App" />,
        Component: ({ row }): ReactElement => (row.app ? <AppDisplayName app={row.app} /> : <C1EmptyIndicator />),
    },
    {
        field: 'type',
        label: <FormattedMessage id="secret_type" defaultMessage="Type" />,
        Component: ({ row }): ReactElement => <ResourceTypeDisplayName
            resourceType={row.appResourceType || AppResourceType.create()}
            margin={false}
        />,
    },
    {
        field: 'identity',
        label: <FormattedMessage id="secret_entitlements" defaultMessage="Identity" />,
        Component: ({ row }): ReactElement => (row.identityAppUserId ? <AppUserDisplayNameWithFetch
            appId={row.appId}
            id={row.identityAppUserId}
        /> : <C1EmptyIndicator />),
    },
    {
        field: 'lastUsedAt',
        flex: 2,
        label: <FormattedMessage id="secret_last_used_at" defaultMessage="Last used at" />,
        Component: ({ row }): ReactElement => (row.lastUsedAt ? <RelativeTimestamp
            date={Timestamp.toDate(row.lastUsedAt)}
            variant="date"
        /> : <C1EmptyIndicator />),
    },
    {
        field: 'expiresAt',
        flex: 2,
        label: <FormattedMessage id="secret_expires_at" defaultMessage="Expiration" />,
        Component: ({ row }): ReactElement => {
            const toolTipTitle = row.secretExpiresAt ? <RelativeTimestamp
                date={Timestamp.toDate(row.secretExpiresAt)}
                variant="date"
            /> : ''
            return (row.secretExpiresAt ? <Stack spacing={1} direction="row">
                <Tooltip
                    title={toolTipTitle}
                    placement="top"
                    arrow
                >
                    <RelativeTimestamp
                        date={Timestamp.toDate(row.secretExpiresAt)}
                        variant="default"
                    />
                </Tooltip>
                {isWithinXDays(row.secretExpiresAt, 15) && <C1Icon icon={TriangleAlert} sx={{ color: 'warning.main' }} />}
            </Stack> : <C1EmptyIndicator />)
        },
    },
    {
        field: 'alertCount',
        isSticky: () => true,
        Component: ({ row }): ReactElement => <InventoryAlertNotification
            appId={row.appId}
            appResourceId={row.id}
            appResourceTypeId={row.appResourceTypeId}
            alertCount={row.alertCount || 0}
        />,
    },
]

export const AppSecrets: FC<AppResourcesPropTypes> = ({ appId, identityAppUserRefs, colsToHide }) => {
    const emptyReq = useMemo<Partial<SearchAppSecretsWithAlertCountRequest>>(() => ({
        appId,
        identityAppUserRefs,
        expandMask: { paths: ['*'] },
    }), [appId, identityAppUserRefs])

    const [filter, setFilter] = useState<SearchAppSecretsWithAlertCountRequest>(
        SearchAppSecretsWithAlertCountRequest.create({
            ...emptyReq,
        }),
    )

    const onFilterChange = useCallback((f: SearchAppSecretsWithAlertCountRequest) => {
        SearchAppSecretsWithAlertCountRequest.mergePartial(f, emptyReq)
        setFilter(f)
    }, [emptyReq])

    // Search
    const search = useMemo(() => <AppSecretsFilter
        onFilterChange={onFilterChange}
        filter={filter}
        hideAppFilter={!!appId}
    />, [onFilterChange, filter, appId])

    const filteredCols = useMemo(
        () => cols.filter((col) => !colsToHide?.includes(col.field as AppSecretResourcesCols)),
        [colsToHide],
    )
    const table = useMemo(() => <C1TableReduxHook<SearchAppSecretsWithAlertCountRequest, AppSecretResourceValue>
        cols={filteredCols}
        request={filter}
        hook={searchAppSecretsApi.useSearchAppSecretsWithAlertCount}
        invalidateHook={searchAppSecretsApi.useInvalidateSearchAppSecretsWithAlertCount}
        emptyState={<EmptyList
            text={<FormattedMessage defaultMessage="No secrets discovered" id="app_user_secrets_empty" />}
        />}
    />, [filter, filteredCols])

    return <List<AppSecretResourceValue> search={search} table={table} />
}
